/**
 * SkillsSelector Component
 * 
 * Displays a list of skills with checkboxes for selection
 * Features:
 * - Search/filter skills
 * - Select All / Clear All buttons
 * - Add custom skills
 */

import React, { useState, useMemo } from "react";
import { 
  List, 
  Checkbox, 
  Input, 
  Button, 
  Space, 
  Typography,
  Tag,
} from "antd";
import { PlusOutlined, SearchOutlined, CheckSquareOutlined, CloseSquareOutlined } from "@ant-design/icons";
import { SkillsSelectorProps } from "../types";
import { SKILLS_LIST_CONFIG, SHARED_STYLES } from "../constants";

const { Text } = Typography;

const SkillsSelector: React.FC<SkillsSelectorProps> = ({
  availableSkills,
  selectedSkills,
  onSkillToggle,
  onSelectAll,
  onClearAll,
  customSkillInput,
  onCustomSkillInputChange,
  onAddCustomSkill,
  disabled = false,
}) => {
  // Search query state
  const [searchQuery, setSearchQuery] = useState("");

  // Combine available skills with any custom skills that were added
  const allSkills = useMemo(() => {
    const skillSet = new Set([...availableSkills, ...selectedSkills]);
    return Array.from(skillSet);
  }, [availableSkills, selectedSkills]);

  // Filter skills based on search query
  const filteredSkills = useMemo(() => {
    if (!searchQuery.trim()) {
      return allSkills;
    }
    const query = searchQuery.toLowerCase().trim();
    return allSkills.filter(skill => 
      skill.toLowerCase().includes(query)
    );
  }, [allSkills, searchQuery]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAddCustomSkill();
    }
  };

  // Check if all visible skills are selected
  const allVisibleSelected = filteredSkills.length > 0 && 
    filteredSkills.every(skill => selectedSkills.includes(skill));

  return (
    <div>
      {/* Header: Search + Select All / Clear All */}
      <div style={{ marginBottom: 8 }}>
        {/* Search Input */}
        <Input
          placeholder="Search skills..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={disabled}
          prefix={<SearchOutlined style={{ color: SHARED_STYLES.textQuaternary }} />}
          allowClear
          size="small"
          style={{ marginBottom: 8 }}
        />
        
        {/* Select All / Clear All + Count */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
        }}>
          <Space size="small">
            <Button
              size="small"
              type="link"
              icon={<CheckSquareOutlined />}
              onClick={onSelectAll}
              disabled={disabled || allSkills.length === 0 || allVisibleSelected}
              style={{ padding: "0 4px", fontSize: SHARED_STYLES.fontSizeSm }}
            >
              Select All
            </Button>
            <Button
              size="small"
              type="link"
              icon={<CloseSquareOutlined />}
              onClick={onClearAll}
              disabled={disabled || selectedSkills.length === 0}
              style={{ padding: "0 4px", fontSize: SHARED_STYLES.fontSizeSm }}
            >
              Clear
            </Button>
          </Space>
          <Tag color={selectedSkills.length > 0 ? "blue" : "default"}>
            {selectedSkills.length} / {allSkills.length}
          </Tag>
        </div>
      </div>

      {/* Skills List */}
      <div
        style={{
          maxHeight: SKILLS_LIST_CONFIG.maxHeight,
          overflowY: "auto",
          border: SHARED_STYLES.border,
          borderRadius: SHARED_STYLES.borderRadius,
          backgroundColor: SHARED_STYLES.containerBackground,
        }}
      >
        {filteredSkills.length > 0 ? (
          <List
            size="small"
            dataSource={filteredSkills}
            renderItem={(skill) => {
              const isSelected = selectedSkills.includes(skill);
              
              const handleClick = () => {
                if (!disabled) {
                  onSkillToggle(skill);
                }
              };
              
              return (
                <List.Item
                  onClick={handleClick}
                  style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    padding: "8px 12px",
                    borderBottom: SHARED_STYLES.border,
                    opacity: disabled ? 0.6 : 1,
                    userSelect: "none",
                    //backgroundColor: isSelected ? SHARED_STYLES.primaryBg : "transparent",
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    width: "100%",
                    justifyContent: "space-between",
                    pointerEvents: "none",
                  }}>
                    <Space>
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        tabIndex={-1}
                      />
                      <Text>{skill}</Text>
                    </Space>
                  </div>
                </List.Item>
              );
            }}
          />
        ) : searchQuery.trim() ? (
          <div
            style={{
              padding: "16px",
              textAlign: "center",
            }}
          >
            <Text type="secondary">
              No skills match "{searchQuery}"
            </Text>
          </div>
        ) : (
          <div
            style={{
              padding: "24px 16px",
              textAlign: "center",
            }}
          >
            <Text type="secondary">
              No skills available. Add custom skills below.
            </Text>
          </div>
        )}
      </div>

      {/* Custom Skill Input - Always visible */}
      <Space 
        style={{ 
          width: "100%", 
          marginTop: SHARED_STYLES.marginSm 
        }}
      >
        <Input
          placeholder="Add custom skill..."
          value={customSkillInput}
          onChange={(e) => onCustomSkillInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          prefix={
            <PlusOutlined 
              style={{ color: SHARED_STYLES.textQuaternary }} 
            />
          }
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          onClick={onAddCustomSkill}
          disabled={disabled || !customSkillInput.trim()}
          icon={<PlusOutlined />}
        >
          Add
        </Button>
      </Space>
    </div>
  );
};

export default SkillsSelector;

